import { api, RTKTags } from '@/store/api'

import Env from '../../../env/env'
import type {
  GetExpiredReportsRequest,
  IExpiredReportsCache,
  IExpiredReportsResponse,
} from '../Enterprise/Enterprise.types'

import type {
  AssignCreditsToWorkspaceRequest,
  UpdateTeamData,
  WorkspaceDetailsRequest,
  WorkspaceDetailsResponse,
  WorkspaceResponse,
  UpdateWorkspaceData,
  RemoveMemberFromTeamRequest,
  getWorkspaceReportsCrudresponse,
  MemberToAssign,
  getAllWorkspaceReportsRequest,
  AllWorkspaceReportsCache,
  getWorkspaceScopedReportsRequest,
} from './Workspace.types'

type PaginatedReportsArgs = {
  workspaceId?: string | null
  teamId?: string | null
  page: number
  pageSize: number
  reportType?: string
  status?: string
  onlyUser?: boolean
  searchByString?: string
  excludeFaceChecks?: boolean
}

const transformPaginatedReports = (
  response: getWorkspaceReportsCrudresponse,
  _meta: unknown,
  arg: PaginatedReportsArgs,
): AllWorkspaceReportsCache => {
  const totalItems = response.info?.totalItems ?? 0
  const items = response.info?.list ?? []
  const totalPages = response.info?.pages ?? 1
  const currentPage = arg.page ?? 1
  return {
    items,
    hasMore: currentPage < totalPages,
    totalItems,
  }
}

const serializeReportsArgs = ({
  endpointName,
  queryArgs,
}: {
  endpointName: string
  queryArgs: PaginatedReportsArgs
}) => {
  const {
    workspaceId = '_',
    teamId = '_',
    pageSize,
    searchByString,
    reportType,
    status,
    onlyUser,
    excludeFaceChecks,
  } = queryArgs
  return `${endpointName}:${workspaceId}:${teamId}:${pageSize}:${searchByString}:${reportType}:${status}:${onlyUser}:${excludeFaceChecks}`
}

const forceRefetchOnPageChange = ({
  currentArg,
  previousArg,
}: {
  currentArg?: PaginatedReportsArgs
  previousArg?: PaginatedReportsArgs
}) => currentArg?.page !== previousArg?.page

export const mergePaginatedReports = (
  currentCache: AllWorkspaceReportsCache,
  newData: AllWorkspaceReportsCache,
  { arg }: { arg: PaginatedReportsArgs },
): AllWorkspaceReportsCache => {
  // Page one always starts the list over. Changing the search or a filter
  // resets the page in an effect, one render after the argument itself changes,
  // so for that render the request carries the new search with the old page
  // number; keying off page one throws that answer away instead of stitching it
  // onto the list.
  //
  // This used to be a check on searchByString, which meant every page of an
  // active search replaced the previous one rather than extending it: scrolling
  // through search results made the first ones disappear.
  if (arg.page === 1) return newData

  if (!newData.items.length) {
    return {
      ...currentCache,
      hasMore: false,
      totalItems: newData.totalItems,
    }
  }

  const getId = (item: AllWorkspaceReportsCache['items'][number]) =>
    item.type === 'facecheck' ? item.data._id : item.data.id
  const newById = new Map(newData.items.map((item) => [getId(item), item]))
  const replaced = currentCache.items.map(
    (item) => newById.get(getId(item)) ?? item,
  )
  const existingIds = new Set(currentCache.items.map(getId))
  const appended = newData.items.filter((item) => !existingIds.has(getId(item)))

  return {
    items: [...replaced, ...appended],
    hasMore: newData.hasMore,
    totalItems: newData.totalItems,
  }
}

export const workspaceApi = api.injectEndpoints({
  endpoints: (builder) => ({
    workspaceDetails: builder.query<
      WorkspaceDetailsResponse,
      WorkspaceDetailsRequest
    >({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v2/enterprises/workspaces/${request.workspaceId}`,
        method: 'GET',
      }),
      transformResponse: (response: any) => {
        return response.info
      },
      providesTags: [RTKTags.workspaceDetails, RTKTags.getWorkspaces],
    }),

    getAllWorkspaceReports: builder.query<
      AllWorkspaceReportsCache,
      getAllWorkspaceReportsRequest
    >({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v2/enterprises/reports${
          request.workspaceId ? `/${request.workspaceId}` : ''
        }${request.teamId ? `/${request.teamId}` : ''}`,
        method: 'GET',
        params: {
          page: request.page,
          pageSize: request.pageSize,
          reportType: request.reportType,
          status: request.status,
          onlyUser: request.onlyUser,
          searchByString: request.searchByString,
        },
      }),
      transformResponse: transformPaginatedReports,
      serializeQueryArgs: serializeReportsArgs,
      forceRefetch: forceRefetchOnPageChange,
      merge: mergePaginatedReports,
      keepUnusedDataFor: 0,
      providesTags: [RTKTags.workspaceDetails, RTKTags.monitoringCenter],
    }),

    getWorkspaces: builder.query<WorkspaceResponse, void>({
      query: () => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v2/enterprises/workspaces`,
        method: 'GET',
      }),
      providesTags: [RTKTags.getWorkspaces],
    }),

    assignCreditsToWorkspace: builder.mutation<
      void,
      AssignCreditsToWorkspaceRequest
    >({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/enterprises/workspaces/${request.workspaceId}/credits`,
        method: 'PUT',
        body: request.values,
      }),
      invalidatesTags: [
        RTKTags.workspaceDetails,
        RTKTags.getWorkspaces,
        RTKTags.userInfo,
        RTKTags.workspaceCredits,
      ],
    }),

    updateTeamData: builder.mutation<void, UpdateTeamData>({
      query: (request: UpdateTeamData) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/enterprises/workspaces/${request.id}/teams/${request.teamId}`,
        method: 'PUT',
        body: {
          name: request.name,
        },
      }),
      invalidatesTags: [RTKTags.workspaceDetails],
    }),

    removeMemberFromTeam: builder.mutation<void, RemoveMemberFromTeamRequest>({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v2/enterprises/workspaces/teams/members/${request.memberId}`,
        method: 'DELETE',
        body: request,
      }),
      invalidatesTags: [RTKTags.workspaceDetails, RTKTags.getWorkspaces],
    }),

    updateWorkspaceData: builder.mutation<void, UpdateWorkspaceData>({
      query: (request: UpdateTeamData) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/enterprises/workspaces/${request.id}`,
        method: 'PUT',
        body: {
          name: request.name,
        },
      }),
      invalidatesTags: [RTKTags.workspaceDetails],
    }),

    inviteMemberToATeam: builder.mutation<void, MemberToAssign>({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v2/enterprises/workspaces/members`,
        method: 'POST',
        body: {
          members: [request],
        },
      }),
      invalidatesTags: [RTKTags.workspaceDetails, RTKTags.getWorkspaces],
    }),

    getWorkspaceScopedReports: builder.query<
      AllWorkspaceReportsCache,
      getWorkspaceScopedReportsRequest
    >({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v2/enterprises/workspaces/${request.workspaceId}/teams/${request.teamId}/subjects`,
        method: 'GET',
        params: {
          page: request.page,
          pageSize: request.pageSize,
          reportType: request.reportType,
          status: request.status,
          onlyUser: request.onlyUser,
          searchByString: request.searchByString,
          ...(request.excludeFaceChecks && { excludeFaceChecks: true }),
        },
      }),
      transformResponse: transformPaginatedReports,
      serializeQueryArgs: serializeReportsArgs,
      forceRefetch: forceRefetchOnPageChange,
      merge: mergePaginatedReports,
      keepUnusedDataFor: 0,
      providesTags: [RTKTags.workspaceDetails, RTKTags.monitoringCenter],
    }),

    getWorkspaceScopedExpiredReports: builder.query<
      IExpiredReportsCache,
      GetExpiredReportsRequest
    >({
      query: (params) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v2/enterprises/workspaces/${params.workspaceId}/teams/${params.teamId}/expired-reports`,
        method: 'GET',
        params: {
          page: params.page,
          pageSize: params.pageSize,
          ...(params.searchByString && {
            searchByString: params.searchByString,
          }),
        },
      }),
      transformResponse: (response: IExpiredReportsResponse) => ({
        list: (response.info?.list ?? []).map((item: any) => ({
          ...item,
          id: item.id || item._id,
        })) as IExpiredReportsCache['list'],
        totalItems: response.info?.totalItems ?? 0,
        reportRetentionDays: response.info?.reportRetentionDays ?? null,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const {
          workspaceId = '_',
          teamId = '_',
          pageSize,
          searchByString,
        } = queryArgs

        return `${endpointName}:${workspaceId}:${teamId}:${pageSize}:${searchByString}`
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page,
      merge: (currentCache, newData, { arg }) => {
        if (
          currentCache &&
          arg.searchByString !== undefined &&
          arg.searchByString !== ''
        ) {
          return newData
        }

        if (!newData.list.length) {
          return { ...currentCache, totalItems: newData.totalItems }
        }

        return {
          list: [...currentCache.list, ...newData.list],
          totalItems: newData.totalItems,
          reportRetentionDays: newData.reportRetentionDays,
        }
      },
      keepUnusedDataFor: 0,
      providesTags: [RTKTags.monitoringCenter],
    }),
  }),
})

export const {
  useWorkspaceDetailsQuery,
  useGetWorkspacesQuery,
  useAssignCreditsToWorkspaceMutation,
  useUpdateTeamDataMutation,
  useRemoveMemberFromTeamMutation,
  useInviteMemberToATeamMutation,
  useUpdateWorkspaceDataMutation,
  useGetAllWorkspaceReportsQuery,
  useGetWorkspaceScopedReportsQuery,
  useGetWorkspaceScopedExpiredReportsQuery,
} = workspaceApi
